import { Box, Button, TextField, ToggleButton, ToggleButtonGroup, Typography } from '@mui/material';
import type { FormEvent } from 'react';
import { useState } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router';
import { useCreateCredentialMutation, useRotateCredentialMutation } from '../../api/admin.api';
import type { CredentialAdded, CredentialInput } from '../../api/admin.api';
import { describeQueryError } from '../../api/describeQueryError.api';
import { AccessKeyGuide } from '../../components/AccessKeyGuide';
import { ReturnLink } from '../../components/ReturnLink';
import { ADMIN_KEYS_PATH, ADMIN_NEW_SOURCE_PATH } from '../../config';
import { contentWidthSx, pageGutterSx, sectionSpacingSx } from '../../theme';
import { AppConnectionResult } from './AppConnectionResult';

/** What GitHub's fine-grained tokens start with. A classic token (`ghp_`) reads everything its owner can, which is more than this needs. */
const FINE_GRAINED_PREFIX = 'github_pat_';

/** The first and last lines of the `.pem` file GitHub gives for an app. The service checks the key itself; this only spares a round trip for an obvious slip. */
const looksLikePrivateKey = (value: string): boolean =>
  /^-----BEGIN (RSA )?PRIVATE KEY-----/.test(value.trim()) && /-----END (RSA )?PRIVATE KEY-----$/.test(value.trim());

type Kind = 'app' | 'token' | 'gitlab';

const WRONG_TOKEN = {
  token: 'That is not a fine-grained token. They start github_pat_. A classic token reads far more than this needs.',
  gitlab: 'That does not look like a GitLab access token. It has no spaces or line breaks.',
} as const;

const HOSTS = { app: 'github.com', token: 'github.com', gitlab: 'gitlab.com' } as const;

/**
 * Whether a secret is plainly the wrong shape for its kind, which spares a round trip. The
 * service checks it too. A GitLab token has no prefix to check, because GitLab lets an
 * instance set its own, so only whitespace gives it away.
 */
const looksWrong = (kind: Kind, secret: string): boolean => {
  if (kind === 'token') {
    return !secret.startsWith(FINE_GRAINED_PREFIX);
  }

  return kind === 'gitlab' ? /\s/.test(secret) : !looksLikePrivateKey(secret);
};

/** Where the admin came from, so a connection made while adding a repository returns to it with the address kept. */
type ReturnState = { url?: string } | null;

/**
 * `/admin/keys/new?kind=app|token|gitlab` — the setup guide beside the form that
 * stores a connection to GitHub or GitLab. **A GitHub App is the default**: it belongs to
 * the organisation, not to a person who may leave, and its key does not expire.
 *
 * **The secret is handled as little as it can be.** It sits in a masked field, goes to
 * the API in one request and is cleared from state the moment that returns. It is never
 * put in the URL, in storage or in an analytics event, and the form says so, because a
 * person pasting a secret into a web page is owed that.
 *
 * **A key GitHub refuses is already stored**, so the form stays open and saves again
 * over the same key, which is how a mistyped App ID is corrected.
 */
export const AdminCredentialNewPage = () => {
  const navigate = useNavigate();
  const returned = (useLocation().state as ReturnState) ?? {};
  const [searchParams, setSearchParams] = useSearchParams();
  const kindParam = searchParams.get('kind');
  const kind: Kind = kindParam === 'token' || kindParam === 'gitlab' ? kindParam : 'app';
  const hostName = kind === 'gitlab' ? 'GitLab' : 'GitHub';

  const [create, createState] = useCreateCredentialMutation();
  const [rotate, rotateState] = useRotateCredentialMutation();
  const [label, setLabel] = useState('');
  const [secret, setSecret] = useState('');
  const [expiresAt, setExpiresAt] = useState('');
  const [appId, setAppId] = useState('');
  const [saved, setSaved] = useState<CredentialAdded | undefined>();

  const isLoading = createState.isLoading || rotateState.isLoading;
  const error = createState.error ?? rotateState.error;
  const refusal = saved?.check?.state === 'refused' ? saved.check.message : undefined;
  const done = saved !== undefined && refusal === undefined;

  const secretIsWrongKind = secret !== '' && looksWrong(kind, secret);
  const canSubmit = secret !== '' && !secretIsWrongKind && !isLoading && label.trim() !== '' && (kind !== 'app' || appId.trim() !== '');

  const idleLabel = saved === undefined ? 'Save access key' : 'Save again';
  const saveLabel = isLoading ? 'Saving' : idleLabel;

  const leave = () => {
    void navigate(returned.url === undefined ? ADMIN_KEYS_PATH : ADMIN_NEW_SOURCE_PATH, returned.url === undefined ? undefined : { state: { url: returned.url } });
  };

  const stored = (result: CredentialAdded) => {
    // Gone from the page before anything else happens.
    setSecret('');
    setSaved(result);

    // An app is shown what GitHub made of it first. A token has nothing to show.
    if (kind !== 'app') {
      leave();
    }
  };

  const inputFor = (): CredentialInput => {
    const common = { label: label.trim(), secret };
    const expiry = expiresAt === '' ? {} : { expiresAt };

    if (kind === 'app') {
      return { kind: 'github-app', host: 'github.com', appId: appId.trim(), ...common };
    }

    return kind === 'gitlab'
      ? { kind: 'gitlab-token', host: 'gitlab.com', ...common, ...expiry }
      : { kind: 'github-fine-grained-pat', host: 'github.com', ...common, ...expiry };
  };

  const submit = (event: FormEvent) => {
    event.preventDefault();

    const input = inputFor();

    const request = saved === undefined ? create(input) : rotate({ id: saved.id, secret, appId: appId.trim() });

    void request
      .unwrap()
      .then(stored)
      .catch(() => undefined);
  };

  const chooseKind = (next: Kind | null) => {
    if (next !== null && saved === undefined) {
      setSecret('');
      setSearchParams({ kind: next }, { replace: true, state: returned });
    }
  };

  return (
    <Box sx={pageGutterSx}>
      <Box sx={{ ...contentWidthSx, paddingBlock: 12 }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
          <ReturnLink to={ADMIN_KEYS_PATH} label="access keys" />
          <Typography variant="h1" component="h1">
            Add an access key
          </Typography>
          <Typography variant="body1" sx={{ color: 'text.secondary', maxWidth: 720 }}>
            A key lets the toolkit read private and internal repositories: a GitHub App, a GitHub token or a GitLab
            token. You are recorded as the person who added it.
          </Typography>
        </Box>

        <Box
          sx={{
            ...sectionSpacingSx,
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', md: 'minmax(0, 1fr) minmax(0, 1fr)' },
            columnGap: 8,
            rowGap: 6,
            alignItems: 'start',
          }}
        >
          <AccessKeyGuide host={kind === 'gitlab' ? 'gitlab' : 'github'} kind={kind === 'app' ? 'app' : 'token'} />

          {done ? (
            <AppConnectionResult check={saved.check} onContinue={leave} />
          ) : (
            <Box component="form" onSubmit={submit} sx={{ display: 'flex', flexDirection: 'column', gap: 4 }} noValidate>
              <ToggleButtonGroup
                exclusive
                size="small"
                aria-label="Kind of connection"
                value={kind}
                disabled={saved !== undefined}
                onChange={(_event, value: Kind | null) => { chooseKind(value); }}
              >
                <ToggleButton value="app">GitHub App (recommended)</ToggleButton>
                <ToggleButton value="token">GitHub token</ToggleButton>
                <ToggleButton value="gitlab">GitLab token</ToggleButton>
              </ToggleButtonGroup>

              <TextField
                label="Label"
                helperText="What it is for, such as the organisation or the repositories it reads."
                value={label}
                onChange={(event) => { setLabel(event.target.value); }}
                required
                disabled={saved !== undefined}
                slotProps={{ htmlInput: { maxLength: 100 } }}
              />

              <TextField label="Host" value={HOSTS[kind]} disabled helperText="A key is only ever sent to this host." />

              {kind === 'app' ? (
                <>
                  <TextField
                    label="App ID"
                    helperText="The App ID, or the Client ID, from the top of the app's settings page. It is not a secret."
                    value={appId}
                    onChange={(event) => { setAppId(event.target.value); }}
                    required
                    autoComplete="off"
                    slotProps={{ htmlInput: { spellCheck: false } }}
                  />

                  <TextField
                    label="Private key"
                    value={secret}
                    onChange={(event) => { setSecret(event.target.value); }}
                    required
                    multiline
                    minRows={4}
                    maxRows={8}
                    autoComplete="off"
                    error={secretIsWrongKind}
                    helperText={
                      secretIsWrongKind
                        ? 'That does not look like the .pem file. Paste all of it, from -----BEGIN to -----END.'
                        : "Stored once, in the toolkit's secret store. It is never shown again."
                    }
                    slotProps={{ htmlInput: { spellCheck: false, style: { WebkitTextSecurity: 'disc', fontFamily: 'monospace' } } }}
                  />
                </>
              ) : (
                <>
                  <TextField
                    label="Token"
                    type="password"
                    value={secret}
                    onChange={(event) => { setSecret(event.target.value); }}
                    required
                    autoComplete="off"
                    error={secretIsWrongKind}
                    helperText={
                      secretIsWrongKind
                        ? WRONG_TOKEN[kind === 'gitlab' ? 'gitlab' : 'token']
                        : "Stored once, in the toolkit's secret store. It is never shown again."
                    }
                    slotProps={{ htmlInput: { spellCheck: false } }}
                  />

                  <TextField
                    label="Expires"
                    type="date"
                    value={expiresAt}
                    onChange={(event) => { setExpiresAt(event.target.value); }}
                    slotProps={{ inputLabel: { shrink: true } }}
                    helperText={`The date you gave the token on ${hostName}. ${hostName} does not tell us, so the page can only show what you enter.`}
                  />
                </>
              )}

              {refusal !== undefined && (
                <Typography role="alert" variant="body2" sx={{ color: 'error.main' }}>
                  {refusal} The key is saved but cannot be used. Correct the App ID or paste the key again, and save.
                </Typography>
              )}

              {error !== undefined && (
                <Typography role="alert" variant="body2" sx={{ color: 'error.main' }}>
                  {describeQueryError(error)}
                </Typography>
              )}

              <Box>
                <Button type="submit" variant="contained" disabled={!canSubmit}>
                  {saveLabel}
                </Button>
              </Box>
            </Box>
          )}
        </Box>
      </Box>
    </Box>
  );
};
